import Seo from "@/components/Seo";
import { Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function HealthEffects() {
  return (
    <div data-testid="health-effects-page">
      <Seo title="Health Effects of Asbestos Exposure" description="Asbestos-related diseases including mesothelioma, asbestosis and lung cancer kill over 4,000 Australians each year. Learn the risks, latency periods and why no safe level of asbestos exposure exists." path="/health-effects" />

      {/* HERO — bold awareness, distinct from Home */}
      <section className="bg-[#0E2A5C] text-white pt-20 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-25" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#9E322B] via-[#B57A1F] to-[#A6CD3C]" />
        <div className="container-x relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-[#9E322B]/15 border border-[#9E322B]/40 rounded-full px-4 py-1.5 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-[#9E322B] animate-pulse" />
                <span className="text-[11.5px] tracking-[0.22em] uppercase font-semibold text-white">Public Health Awareness</span>
              </div>
              <h1 className="text-[44px] md:text-[64px] font-bold leading-[0.98]">
                The silent killer<br />
                that takes <span className="text-[#9E322B]">4,000+</span> Australian lives a year.
              </h1>
              <p className="mt-6 text-white/80 text-[16.5px] leading-relaxed max-w-2xl">
                Asbestos Related Diseases (ARD) is one of Australia's most significant ongoing health crises — yet exposure continues, often unknowingly, in homes, schools, public infrastructure and workplaces.
              </p>
            </div>
            <div className="lg:col-span-5 grid grid-cols-2 gap-3 lg:mt-4">
              {[
                { k: "4,000+", v: "Deaths per year", bg: "bg-[#9E322B]" },
                { k: "3,000+", v: "ACM products", bg: "bg-[#B57A1F]" },
                { k: "10–40", v: "Year latency", bg: "bg-[#1F6B8A]" },
                { k: "6.2M Tons", v: "In the built environment", bg: "bg-[#A6CD3C]" },
              ].map((s) => (
                <div key={s.k} className="bg-white/[0.06] border border-white/15 rounded-2xl p-5 relative overflow-hidden backdrop-blur">
                  <div className={`absolute left-0 top-0 w-1 h-full ${s.bg}`} />
                  <div className="text-[26px] font-bold text-white leading-none">{s.k}</div>
                  <div className="text-[12px] text-white/70 mt-2">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <article className="lg:col-span-8 prose-isgs">
            <div className="eyebrow mb-4">The science</div>
            <h2 className="text-[32px] md:text-[40px] text-[#0E2A5C] mb-7 leading-tight">
              Why asbestos fibres are uniquely dangerous to human lungs.
            </h2>
            <div className="space-y-5 text-[16px] leading-relaxed text-[#0B0F1A]/85">
              <p>
                <strong>Asbestos</strong> is a broad term for a naturally occurring mineral that was extensively manufactured into over <strong>3,000+ products</strong> worldwide. Australia historically recorded one of the highest per-capita usage rates of asbestos-containing materials (ACM) globally.
              </p>
              <p>
                Asbestos products can release microscopic fibres into the air which are unknowingly inhaled. Once inhaled, the human body is unable to break these fibres down or expel them — they accumulate in lung tissue over time, triggering inflammation, scarring and cellular changes.
              </p>
              <p>
                There is <strong className="text-[#0E2A5C]">no known safe level of asbestos exposure</strong>. Even brief or incidental contact with asbestos-containing materials can carry inherent health risk. Prolonged or significant exposure is directly linked to serious and often fatal Asbestos Related Diseases (ARDs) — all of which are <em>progressive, debilitating and currently incurable</em>.
              </p>
            </div>

            <div className="mt-12 bg-[#F7F8F3] border-l-4 border-[#A6CD3C] p-7 rounded-r-2xl">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="text-[#0E2A5C]" size={22} />
                <h3 className="text-[20px] font-semibold text-[#0E2A5C] m-0">Latency: 10 to 40+ years</h3>
              </div>
              <p className="text-[#5B6478] text-[15px] leading-relaxed m-0">
                Symptoms typically present 10–40+ years after exposure. By the time clinical signs appear, the disease is usually well-established — making asbestos a true silent killer.
              </p>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="bg-[#F7F8F3] border border-[#0E2A5C]/10 rounded-2xl p-7 sticky top-32">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#9E322B] font-bold mb-5">Key facts</div>
              <ul className="space-y-5">
                <li className="border-l-[3px] border-[#9E322B] pl-4">
                  <div className="text-[15px] text-[#0E2A5C] font-semibold">No safe level of exposure</div>
                  <div className="text-[#5B6478] text-[13px] mt-1">Even brief contact carries inherent risk</div>
                </li>
                <li className="border-l-[3px] border-[#B57A1F] pl-4">
                  <div className="text-[15px] text-[#0E2A5C] font-semibold">Fibres are permanent</div>
                  <div className="text-[#5B6478] text-[13px] mt-1">The body cannot expel inhaled fibres</div>
                </li>
                <li className="border-l-[3px] border-[#1F6B8A] pl-4">
                  <div className="text-[15px] text-[#0E2A5C] font-semibold">Latency is decades</div>
                  <div className="text-[#5B6478] text-[13px] mt-1">10–40+ years before symptoms appear</div>
                </li>
                <li className="border-l-[3px] border-[#A6CD3C] pl-4">
                  <div className="text-[15px] text-[#0E2A5C] font-semibold">Currently incurable</div>
                  <div className="text-[#5B6478] text-[13px] mt-1">ARDs are progressive and debilitating</div>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="section-pad bg-[#0E2A5C] text-white">
        <div className="container-x text-center max-w-3xl mx-auto">
          <h2 className="text-[32px] md:text-[44px] mb-6">Suspect asbestos in your home or workplace?</h2>
          <p className="text-white/75 mb-9 text-[16px]">Don't take risks. A short inspection by a certified ISGS consultant can confirm or rule out exposure.</p>
          <Link to="/contact" className="btn-primary" data-testid="health-cta-inspection">
            Book an Inspection <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <section className="section-pad bg-[#F7F8F3] relative overflow-hidden">
        {/* Background — weathered asbestos-cement roof (Super Six), muted but visible */}
        <div
          className="absolute inset-0 bg-center bg-cover pointer-events-none"
          style={{
            backgroundImage:
              "url('https://customer-assets-eiarnc6j.emergentagent.net/job_draft-preview-11/artifacts/8bdpfzf9_Super%20six%20garage.jpeg')",
            opacity: 0.35,
          }}
          aria-hidden
        />
        {/* Soft cream tint so text and card remain fully readable */}
        <div
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#F7F8F3]/70 via-[#F7F8F3]/55 to-[#F7F8F3]/80"
          aria-hidden
        />

        <div className="container-x relative z-10">
          <div className="max-w-2xl mb-10 md:mb-14">
            <div className="eyebrow mb-4">The diseases</div>
            <h2 className="text-[34px] md:text-[44px] text-[#0E2A5C] leading-tight">
              Primary asbestos-related diseases.
            </h2>
          </div>

          {/* Illustrated lung diagram — labelled with the four primary ARDs */}
          <figure
            className="relative mx-auto max-w-5xl bg-white rounded-3xl border border-[#0E2A5C]/10 shadow-[0_28px_60px_-28px_rgba(14,42,92,0.28)] overflow-hidden"
            data-testid="diseases-diagram"
          >
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_draft-preview-11/artifacts/pbf7o4fs_image.png"
              alt="Illustrated human lungs labelled with the four primary asbestos-related diseases — Mesothelioma, Asbestos-Induced Lung Cancer, Asbestosis and Pleural Plaques — with concise clinical descriptions of each condition."
              width="1280"
              height="854"
              loading="lazy"
              decoding="async"
              className="block w-full h-auto"
            />
            <figcaption className="sr-only">
              Diagram of the four primary asbestos-related diseases: Mesothelioma (aggressive cancer of the lining of the lungs, abdomen or heart), Asbestos-Induced Lung Cancer (malignant lung tumours from inhaled asbestos fibres), Asbestosis (chronic progressive scarring of lung tissue) and Pleural Plaques (thickening of the pleural lining and marker of past exposure).
            </figcaption>
          </figure>
        </div>
      </section>
    </div>
  );
}
